import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  RotateCcw, DollarSign, Receipt, Package, TrendingUp, AlertTriangle,
  PackageCheck, Send, PlayCircle, MapPin, Truck, Zap, History,
  ClipboardList, BarChart3, ShoppingBag, Users, ListChecks, ExternalLink,
} from "lucide-react";
import api from "@/lib/api";

/* =====================================================================
   Phase 3A · Owner Overview Dashboard — Sales Command Center
   ---------------------------------------------------------------------
   Frontend-only redesign that consumes exclusively existing endpoints:
     • POST /api/admin/sales-dashboard/report     (money + channel + top)
     • GET  /api/admin/home-dashboard             (needs attention)
     • POST /api/admin/all-orders                 (recent large sales)
   Each section renders independently and degrades gracefully.
   The Today tab is unchanged — this is the `dashboard` tab only.
   ===================================================================== */

const PERIODS = [
  { key: "today",      preset: "today",      label: "Today"       },
  { key: "this_week",  preset: "last_7",     label: "This Week"   },
  { key: "this_month", preset: "this_month", label: "This Month"  },
];

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num   = (v) => Number(v || 0).toLocaleString();
const safe  = (v, fallback = "—") => (v === null || v === undefined || v === "" ? fallback : v);

function todayHuman() {
  const d = new Date();
  return d.toLocaleDateString(undefined, {
    weekday: "long", year: "numeric", month: "short", day: "numeric",
  });
}

/* ─── Section shell ──────────────────────────────────────────────── */

function SectionCard({ title, subtitle, right, testid, children }) {
  return (
    <div className="card-fun p-5 md:p-6 min-w-0" data-testid={testid}>
      <div className="flex flex-wrap items-start justify-between gap-2 mb-4">
        <div className="min-w-0">
          {subtitle && <div className="kicker kicker-red text-[10px]">{subtitle}</div>}
          <h3 className="font-display text-lg">{title}</h3>
        </div>
        {right}
      </div>
      <div>{children}</div>
    </div>
  );
}

function LocalError({ msg, onRetry }) {
  return (
    <div className="rounded-xl border-2 border-dashed border-red-700 bg-red-50 text-red-900 p-3 text-sm flex items-center justify-between gap-3"
         data-testid="section-error">
      <div className="flex items-center gap-2"><AlertTriangle size={14} /> {msg || "Couldn't load this section."}</div>
      {onRetry && (
        <button onClick={onRetry} className="btn-ghost text-xs" data-testid="section-retry">
          <RotateCcw size={12} /> Retry
        </button>
      )}
    </div>
  );
}

function Skeleton({ h = "h-6", w = "w-full" }) {
  return <div className={`${h} ${w} bg-gray-100 rounded animate-pulse`} />;
}

/* ─── 1. Sales snapshot ──────────────────────────────────────────── */

function Kpi({ label, value, sub, onClick, testid }) {
  const Cmp = onClick ? "button" : "div";
  return (
    <Cmp
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`card-fun p-4 text-left ${onClick ? "hover:-translate-y-0.5 hover:shadow-[4px_4px_0_var(--ink)] transition" : ""}`}
      data-testid={testid}
    >
      <div className="kicker text-[10px] opacity-80">{label}</div>
      <div className="font-display text-3xl md:text-4xl mt-2 tabular-nums">{value}</div>
      <div className="text-[11px] text-[var(--smoke)] mt-1 tabular-nums">{sub}</div>
    </Cmp>
  );
}

function SalesSnapshot({ period, report, loading, error, onRetry, onOpenDashboard }) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="snapshot-loading">
        <Skeleton h="h-24" /><Skeleton h="h-24" /><Skeleton h="h-24" /><Skeleton h="h-24" />
      </div>
    );
  }
  if (error) return <LocalError msg="Snapshot unavailable." onRetry={onRetry} />;
  const kpis = report?.kpis || {};
  const gross = Number(kpis.gross_sales || 0);
  const txn   = Number(kpis.transactions || 0);
  const units = Number(kpis.units_sold || 0);
  const avg   = txn > 0 ? gross / txn : 0;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="snapshot-grid">
      <Kpi label="Gross Sales" value={money(gross)} sub={period.label}
           onClick={onOpenDashboard} testid="kpi-gross" />
      <Kpi label="Transactions" value={num(txn)} sub={period.label}
           onClick={onOpenDashboard} testid="kpi-txn" />
      <Kpi label="Units Sold" value={num(units)} sub={period.label}
           onClick={onOpenDashboard} testid="kpi-units" />
      <Kpi label="Avg Sale" value={money(avg)} sub={txn ? "per transaction" : "—"}
           onClick={onOpenDashboard} testid="kpi-avg" />
    </div>
  );
}

/* ─── 2. Needs attention ─────────────────────────────────────────── */

const ATTENTION_ICONS = {
  awaiting_payment:    Send,
  payment_submitted:   PackageCheck,
  payment_verified:    PlayCircle,
  preparing:           ShoppingBag,
  ready_pickup:        MapPin,
  ready_ship:          Truck,
  ready_unknown:       AlertTriangle,
  stale_orders:        AlertTriangle,
  missing_information: AlertTriangle,
};

const ATTENTION_ORDER = [
  "awaiting_payment", "payment_submitted", "payment_verified", "preparing",
  "ready_pickup", "ready_ship", "stale_orders", "missing_information",
];

function NeedsAttention({ data, loading, error, onRetry }) {
  const nav = useNavigate();
  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2"
           data-testid="attention-loading">
        {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} h="h-20" />)}
      </div>
    );
  }
  if (error) return <LocalError msg="Needs-attention counts unavailable." onRetry={onRetry} />;
  const cards = (data?.cards || []).filter(
    (c) => c.section === "needs_attention" && ATTENTION_ORDER.includes(c.key)
  );
  const byKey = Object.fromEntries(cards.map((c) => [c.key, c]));
  const openCard = (c) => {
    const v = Number(c.value || 0);
    if (!v) return;
    if (c.view) {
      nav(`/admin/orders?preset=all_time&view=${c.view}`); return;
    }
    const params = new URLSearchParams({ preset: "all_time" });
    if (c.status) params.set("status", c.status);
    if (c.fulfillment_group) params.set("fulfillment_group", c.fulfillment_group);
    nav(`/admin/orders?${params.toString()}`);
  };
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2"
         data-testid="attention-grid">
      {ATTENTION_ORDER.map((k) => {
        const c = byKey[k];
        if (!c) return null;
        const Icon = ATTENTION_ICONS[k] || Send;
        const v = Number(c.value || 0);
        const isUrgent = !!c.urgent && v > 0;
        const quiet = v === 0;
        return (
          <button
            key={k}
            onClick={() => openCard(c)}
            disabled={quiet}
            className={[
              "text-left p-3 rounded-xl border-2 transition",
              quiet
                ? "border-[var(--line)] bg-white text-[var(--smoke)] cursor-default"
                : isUrgent
                  ? "border-red-900 bg-red-50 text-red-900 hover:-translate-y-0.5 hover:shadow-[4px_4px_0_var(--ink)]"
                  : "border-[var(--ink)] bg-[var(--panini-yellow-soft)] text-[var(--ink)] hover:-translate-y-0.5 hover:shadow-[4px_4px_0_var(--ink)]",
            ].join(" ")}
            data-testid={`attention-${k}`}
          >
            <Icon size={14} />
            <div className="font-display text-2xl mt-2 tabular-nums leading-none">{num(v)}</div>
            <div className="text-[10px] uppercase tracking-widest font-bold mt-1 opacity-80">{c.label}</div>
          </button>
        );
      })}
    </div>
  );
}

/* ─── 3. Sales by channel ────────────────────────────────────────── */

const CHANNEL_ROW_ORDER = [
  "store", "events", "website", "shopify", "ebay", "sales_order",
];

function SalesByChannel({ report, loading, error, onRetry, onOpenChannel }) {
  if (loading) return <Skeleton h="h-56" />;
  if (error) return <LocalError msg="Channels unavailable." onRetry={onRetry} />;
  const rows = report?.by_channel || [];
  const byKey = Object.fromEntries(rows.map((r) => [r.channel, r]));
  const ordered = [
    ...CHANNEL_ROW_ORDER.map((k) => byKey[k]).filter(Boolean),
    ...rows.filter((r) => !CHANNEL_ROW_ORDER.includes(r.channel)),
  ];
  if (!ordered.length) {
    return <div className="text-sm text-[var(--smoke)] py-2">No channel sales in this period.</div>;
  }
  const COLS = "grid-cols-[minmax(0,1fr)_110px_90px_90px]";
  return (
    <div className="overflow-x-auto">
      <div className="min-w-[460px]">
        <div className={`grid ${COLS} items-center gap-2 px-2 py-2 border-b-2 border-[var(--ink)] text-[10px] uppercase tracking-widest text-[var(--smoke)] font-semibold`}
             data-testid="channel-header">
          <span>Channel</span>
          <span className="text-right">Gross</span>
          <span className="text-right">Txn</span>
          <span className="text-right">Units</span>
        </div>
        <div className="divide-y divide-gray-100">
          {ordered.map((r) => (
            <button
              key={r.channel}
              onClick={() => onOpenChannel?.(r.channel)}
              className={`grid ${COLS} items-center gap-2 px-2 py-2 w-full text-left text-[12px] hover:bg-[var(--panini-yellow-soft)]`}
              data-testid={`channel-row-${r.channel}`}
            >
              <span className="truncate font-semibold">{r.label}</span>
              <span className="text-right tabular-nums">{money(r.gross)}</span>
              <span className="text-right tabular-nums">{num(r.txn)}</span>
              <span className="text-right tabular-nums">{num(r.units)}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── 4. Top performance ─────────────────────────────────────────── */

function TopList({ title, testid, rows, nameKey, onOpen }) {
  const COLS = "grid-cols-[minmax(0,1fr)_110px_80px_70px]";
  const top = (rows || []).slice(0, 5);
  if (!top.length) {
    return (
      <div data-testid={testid}>
        <div className="kicker kicker-red text-[10px]">Top</div>
        <h4 className="font-display text-base mb-2">{title}</h4>
        <div className="text-sm text-[var(--smoke)] py-2">No sales in this period.</div>
      </div>
    );
  }
  return (
    <div data-testid={testid}>
      <div className="flex items-end justify-between mb-2 gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="kicker kicker-red text-[10px]">Top</div>
          <h4 className="font-display text-base">{title}</h4>
        </div>
        <button onClick={onOpen} className="btn-ghost text-[11px] whitespace-nowrap" data-testid={`${testid}-open`}>
          View in Sales Dashboard <ExternalLink size={11} />
        </button>
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[440px]">
          <div className={`grid ${COLS} items-center gap-2 px-2 py-1.5 border-b border-[var(--ink)] text-[10px] uppercase tracking-widest text-[var(--smoke)] font-semibold`}>
            <span>Name</span>
            <span className="text-right">Gross</span>
            <span className="text-right">Units</span>
            <span className="text-right">Txn</span>
          </div>
          <div className="divide-y divide-gray-100">
            {top.map((r, i) => (
              <div key={(r[nameKey] || i)} className={`grid ${COLS} items-center gap-2 px-2 py-2 text-[12px]`}>
                <span className="truncate" title={r[nameKey]}>{safe(r[nameKey])}</span>
                <span className="text-right tabular-nums">{money(r.dollars ?? r.gross)}</span>
                <span className="text-right tabular-nums">{num(r.units ?? r.qty)}</span>
                <span className="text-right tabular-nums">{num(r.txn)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function TopPerformance({ report, loading, error, onRetry, onOpenDashboard }) {
  if (loading) return <div className="grid md:grid-cols-2 gap-6"><Skeleton h="h-56" /><Skeleton h="h-56" /></div>;
  if (error) return <LocalError msg="Top performance unavailable." onRetry={onRetry} />;
  const topCategories = (report?.by_category || []).map((c) => ({
    name:     c.category_label || c.category,
    dollars:  c.dollars,
    units:    c.units,
    txn:      c.txn,
  }));
  const topItems = (report?.by_item || []).map((it) => ({
    name:     it.title || it.item,
    dollars:  it.dollars,
    units:    it.qty,
    txn:      it.txn,
  }));
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <TopList title="Categories" testid="top-categories" rows={topCategories}
                nameKey="name" onOpen={onOpenDashboard} />
      <TopList title="Products"   testid="top-products"   rows={topItems}
                nameKey="name" onOpen={onOpenDashboard} />
    </div>
  );
}

/* ─── 5. Recent large sales ──────────────────────────────────────── */

function RecentLargeSales({ rows, loading, error, onRetry }) {
  const nav = useNavigate();
  const COLS = "grid-cols-[120px_minmax(0,1fr)_140px_120px_90px]";
  if (loading) return <Skeleton h="h-40" />;
  if (error) return <LocalError msg="Recent sales unavailable." onRetry={onRetry} />;
  if (!rows?.length) {
    return (
      <div className="text-sm text-[var(--smoke)] py-2"
           data-testid="recent-empty">
        No qualifying sales for this period.
      </div>
    );
  }
  const top = rows.slice(0, 5);
  const fmtTime = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
    catch { return iso; }
  };
  return (
    <div className="overflow-x-auto">
      <div className="min-w-[700px]">
        <div className={`grid ${COLS} items-center gap-2 px-2 py-2 border-b-2 border-[var(--ink)] text-[10px] uppercase tracking-widest text-[var(--smoke)] font-semibold`}
             data-testid="recent-header">
          <span>Order</span>
          <span>Customer / Channel</span>
          <span className="text-right">When</span>
          <span className="text-right">Summary</span>
          <span className="text-right">Gross</span>
        </div>
        <div className="divide-y divide-gray-100">
          {top.map((o) => {
            // `/sales-dashboard/report.transactions[]` row shape:
            //   public_id · submitted_at · channel_label · customer · summary · total · payment_method
            const pid     = o.public_id;
            const cust    = o.customer || "—";
            const channel = o.channel_label || o.channel || "—";
            const when    = o.submitted_at;
            // Pre-tax subtotal (same basis as kpis.gross_sales).
            // Falls back to `total` ONLY if the backend hasn't yet
            // exposed `gross` — Production always has gross.
            const amount  = typeof o.gross === "number" ? o.gross : Number(o.total || 0);
            const summary = o.summary || "—";
            return (
              <button
                key={pid}
                onClick={() => nav(`/admin/fulfillment/${pid}`)}
                className={`grid ${COLS} items-center gap-2 px-2 py-2 w-full text-left text-[12px] hover:bg-[var(--panini-yellow-soft)]`}
                data-testid={`recent-row-${pid}`}
              >
                <span className="font-mono text-[11px] text-[var(--panini-red)] truncate" title={pid}>{pid}</span>
                <span className="truncate">
                  <span className="font-semibold">{cust}</span>
                  <span className="text-[var(--smoke)]"> · {channel}</span>
                </span>
                <span className="text-right tabular-nums text-[var(--smoke)]">{fmtTime(when)}</span>
                <span className="text-right tabular-nums text-[var(--smoke)] truncate" title={summary}>{summary}</span>
                <span className="text-right tabular-nums font-semibold">{money(amount)}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ─── 6. Quick actions ───────────────────────────────────────────── */

const QUICK_ACTIONS = [
  { key: "new_sale",       label: "New Sale",       to: "/admin/new-sale",         Icon: ShoppingBag,
    tone: "bg-[var(--panini-yellow)] text-[var(--ink)] border-[var(--ink)]" },
  { key: "quick_sale",     label: "Quick Sale",     to: "/admin/quick-sale",       Icon: Zap,
    tone: "bg-red-600 text-white border-red-900" },
  { key: "sales_dashboard", label: "Sales Dashboard", to: "/admin/sales-dashboard", Icon: BarChart3,
    tone: "bg-emerald-100 text-emerald-900 border-emerald-900" },
  { key: "all_orders",     label: "All Orders",     to: "/admin/orders",           Icon: ListChecks,
    tone: "bg-blue-100 text-blue-900 border-blue-900" },
  { key: "fulfillment",    label: "Fulfillment",    to: "/admin?tab=fulfillment",  Icon: Truck,
    tone: "bg-emerald-50 text-emerald-900 border-emerald-900" },
  { key: "customers",      label: "Customers",      to: "/admin?tab=collectors",   Icon: Users,
    tone: "bg-white text-[var(--ink)] border-[var(--ink)]" },
];

function QuickActions() {
  const nav = useNavigate();
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3"
         data-testid="quick-actions">
      {QUICK_ACTIONS.map((a) => (
        <button
          key={a.key}
          onClick={() => nav(a.to)}
          data-testid={`quick-${a.key}`}
          className={`text-left p-4 border-2 rounded-2xl transition hover:-translate-y-0.5 hover:shadow-[4px_4px_0_var(--ink)] ${a.tone}`}
        >
          <a.Icon size={18} />
          <div className="mt-3 font-display text-lg leading-tight">{a.label}</div>
        </button>
      ))}
    </div>
  );
}

/* ─── Container ──────────────────────────────────────────────────── */

export default function Overview() {
  const nav = useNavigate();
  const [periodKey, setPeriodKey] = useState("today");
  const period = useMemo(() => PERIODS.find((p) => p.key === periodKey), [periodKey]);

  // Section-local states so one failed call never blanks the page.
  const [report, setReport]           = useState(null);
  const [reportLoading, setReportLoading] = useState(true);
  const [reportError, setReportError] = useState(null);

  const [home, setHome]               = useState(null);
  const [homeLoading, setHomeLoading] = useState(true);
  const [homeError, setHomeError]     = useState(null);

  const [recent, setRecent]           = useState(null);
  const [recentLoading, setRecentLoading] = useState(true);
  const [recentError, setRecentError] = useState(null);

  const loadReport = useCallback(async () => {
    setReportLoading(true); setReportError(null);
    setRecentLoading(true); setRecentError(null);
    try {
      // Single source of truth: /sales-dashboard/report.transactions[]
      // is already revenue-qualified (qualifies_for_revenue + channel
      // classification), so Recent Large Sales inherits the SAME
      // business/revenue truth as the KPIs — no fixtures, no $0 rows,
      // no cancelled/abandoned/refunded leakage, no second rule.
      const r = await api.post("/admin/sales-dashboard/report", {
        preset: period.preset, channel: "all",
        item_sort: "dollars", item_limit: 20, transactions_limit: 200,
      });
      setReport(r.data);
      const tx = Array.isArray(r.data?.transactions) ? r.data.transactions : [];
      // Phase 3A · Recent Large Sales reconciliation.
      // `gross` is the SAME pre-tax subtotal that feeds kpis.gross_sales
      // / by_channel / by_category / by_item on the Sales Dashboard, so
      // displaying it here guarantees the amount basis matches every
      // other number on the Overview.  Fallback to `total` is kept only
      // for forward-compat (older backend that doesn't yet expose
      // `gross`); Production has gross.
      const amtOf = (t) => (
        typeof t.gross === "number" ? t.gross : Number(t.total || 0)
      );
      const topFive = [...tx]
        .filter((t) => amtOf(t) > 0)
        .sort((a, b) => amtOf(b) - amtOf(a))
        .slice(0, 5);
      setRecent(topFive);
    } catch (e) {
      setReportError(e);
      setRecentError(e);
    } finally {
      setReportLoading(false);
      setRecentLoading(false);
    }
  }, [period.preset]);

  const loadHome = useCallback(async () => {
    setHomeLoading(true); setHomeError(null);
    try {
      const r = await api.get("/admin/home-dashboard");
      setHome(r.data);
    } catch (e) { setHomeError(e); } finally { setHomeLoading(false); }
  }, []);

  // Period change refetches the sales report (which also drives
  // Recent Large Sales); home-dashboard is period-agnostic.
  useEffect(() => { loadReport(); }, [loadReport]);
  useEffect(() => { loadHome(); }, [loadHome]);

  const refreshAll = () => { loadReport(); loadHome(); };

  const openDashboardForPeriod = () => {
    nav(`/admin/sales-dashboard?preset=${period.preset}`);
  };
  const openChannel = (channel) => {
    nav(`/admin/sales-dashboard?preset=${period.preset}&channel=${channel}`);
  };

  return (
    <div className="space-y-6" data-testid="admin-overview">
      {/* 1. Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3"
           data-testid="overview-header">
        <div>
          <div className="kicker kicker-red">Owner</div>
          <h2 className="font-display text-3xl">Owner Overview</h2>
          <p className="text-sm text-[var(--smoke)] mt-1" data-testid="overview-date">
            {todayHuman()}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-xl border-2 border-[var(--ink)] bg-white p-1"
               role="tablist" aria-label="Period" data-testid="period-selector">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPeriodKey(p.key)}
                role="tab"
                aria-selected={periodKey === p.key}
                data-testid={`period-${p.key}`}
                className={`px-3 py-1.5 rounded-lg text-sm font-bold transition ${
                  periodKey === p.key
                    ? "bg-[var(--ink)] text-[var(--panini-yellow)]"
                    : "text-[var(--ink)] hover:bg-[var(--panini-yellow-soft)]"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button onClick={refreshAll} className="btn-ghost text-sm" data-testid="overview-refresh">
            <RotateCcw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* 2. Sales snapshot */}
      <SalesSnapshot
        period={period}
        report={report}
        loading={reportLoading}
        error={reportError}
        onRetry={loadReport}
        onOpenDashboard={openDashboardForPeriod}
      />

      {/* 3. Needs attention */}
      <SectionCard title="Needs Attention" subtitle="Operational"
                    testid="section-attention">
        <NeedsAttention
          data={home} loading={homeLoading} error={homeError}
          onRetry={loadHome}
        />
      </SectionCard>

      {/* 4. + 5. Channel + Top Performance side-by-side on desktop */}
      <div className="grid lg:grid-cols-2 gap-6">
        <SectionCard title="Sales by Channel" subtitle={period.label}
                      testid="section-channels"
                      right={<button onClick={openDashboardForPeriod} className="btn-ghost text-[11px]">
                               Open <ExternalLink size={11} />
                             </button>}>
          <SalesByChannel
            report={report} loading={reportLoading} error={reportError}
            onRetry={loadReport} onOpenChannel={openChannel}
          />
        </SectionCard>
        <SectionCard title="Top Performance" subtitle={period.label}
                      testid="section-top">
          <TopPerformance
            report={report} loading={reportLoading} error={reportError}
            onRetry={loadReport} onOpenDashboard={openDashboardForPeriod}
          />
        </SectionCard>
      </div>

      {/* 6. Recent large sales */}
      <SectionCard title="Recent Large Sales" subtitle={period.label}
                    testid="section-recent">
        <RecentLargeSales
          rows={recent} loading={recentLoading} error={recentError}
          onRetry={loadReport}
        />
      </SectionCard>

      {/* 7. Quick actions */}
      <SectionCard title="Quick Actions" subtitle="Jump to"
                    testid="section-actions">
        <QuickActions />
      </SectionCard>
    </div>
  );
}
